// i18n bootstrap. W29 adds Malayalam, Kannada, Telugu alongside the original
// Tamil + English. Tamil remains the source/fallback language so untranslated
// keys in ml/kn/te degrade gracefully to Tamil first, then English.

import { useEffect, useReducer } from 'react';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import ta_common from './ta/common.json';
import en_common from './en/common.json';
import ml_common from './ml/common.json';
import kn_common from './kn/common.json';
import te_common from './te/common.json';

const LANG_KEY = 'chitos_lang';

export type Locale = 'ta' | 'ml' | 'kn' | 'te' | 'en';
const SUPPORTED: Locale[] = ['ta', 'ml', 'kn', 'te', 'en'];

function normalize(raw: string | null): Locale {
  if (raw && (SUPPORTED as string[]).includes(raw)) return raw as Locale;
  return 'ta';
}

const stored = typeof window !== 'undefined' ? window.localStorage.getItem(LANG_KEY) : null;
const initial = normalize(stored);

void i18next.use(initReactI18next).init({
  lng: initial,
  fallbackLng: ['ta', 'en'],
  resources: {
    ta: { common: ta_common },
    en: { common: en_common },
    ml: { common: ml_common },
    kn: { common: kn_common },
    te: { common: te_common },
  },
  ns: ['common'],
  defaultNS: 'common',
  interpolation: { escapeValue: false },
});

export function set_language(lang: Locale): void {
  void i18next.changeLanguage(lang);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LANG_KEY, lang);
    window.document.documentElement.lang = lang;
  }
}

export function current_language(): Locale {
  const cur = i18next.language;
  if ((SUPPORTED as string[]).includes(cur)) return cur as Locale;
  return 'ta';
}

/** Reactive variant of {@link current_language}. Subscribes to i18next's
 *  `languageChanged` event so a component re-renders the instant the language
 *  toggles — needed for components (e.g. the Shell) that read the language but
 *  don't otherwise call `useTranslation`, whose toggle highlight would
 *  otherwise stay stale until the next navigation. */
export function use_language(): Locale {
  const [, force] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    i18next.on('languageChanged', force);
    return () => {
      i18next.off('languageChanged', force);
    };
  }, []);
  return current_language();
}

/** Per-locale font-family CSS class. Studio components apply this to text
 *  blocks that need script-aware rendering. */
export function font_class_for(locale: Locale): string {
  switch (locale) {
    case 'ta':
      return 'font-tamil';
    case 'ml':
      return 'font-malayalam';
    case 'kn':
      return 'font-kannada';
    case 'te':
      return 'font-telugu';
    case 'en':
    default:
      return 'font-sans';
  }
}

/** Human-readable native label for the language switcher. */
export function locale_label(locale: Locale): string {
  switch (locale) {
    case 'ta':
      return 'தமிழ்';
    case 'ml':
      return 'മലയാളം';
    case 'kn':
      return 'ಕನ್ನಡ';
    case 'te':
      return 'తెలుగు';
    case 'en':
      return 'English';
  }
}

export const SUPPORTED_LOCALES = SUPPORTED;
export { i18next };
